import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Checkbox, Typography } from "@mui/material/";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { uniq, countBy } from "lodash";
import { helpers, sort } from "../../../common/lib";
import { strings } from "../../../common/i18n";

/**
 *  Hook for user table data and operations
 */

export default () => {
    const params = useParams();

    const [data, setData] = useState([]);

    const { accounts, branches, branchLocations, locations, userLocations } =
        useSelector((state) => state);

    const groups = accounts.item ? accounts.item.groups : [];

    // Sort selected rows first then alphabetically by location
    const hasSelection = useCallback(
        (row) => groups.some((group) => row[group.name]?.selected),
        [groups]
    );

    const sortRowsBySelection = useCallback(
        (rows) => {
            const priority = (row) => (hasSelection(row) ? 0 : 1);
            return [...rows].sort((a, b) => {
                const pA = priority(a);
                const pB = priority(b);
                if (pA !== pB) return pA - pB;
                return sort.string(a.location, b.location);
            });
        },
        [hasSelection]
    );

    useEffect(() => {
        const userId = Number(params.userId);
        const forUser = userLocations.data.filter(
            (item) => item.user === userId
        );

        const next = locations.data.map((location) => {
            const branchNames = uniq(
                branchLocations.data
                    .filter((i) => i.location === location.id)
                    .map((i) => {
                        const branch = helpers.getListItemForId(
                            branches,
                            i.branch
                        );
                        return branch.name;
                    })
                    .sort(sort.string)
            );
            const forLocation = forUser.filter(
                (item) => item.location === location.id
            );
            return {
                branches: branchNames.join(" "),
                branchNames,
                location: location.name,
                locationId: location.id,
                ...groups.reduce(
                    (aggregate, item) => ({
                        ...aggregate,
                        [item.name]: {
                            groupId: item.id,
                            selected: Boolean(
                                forLocation.find((i) => i.group === item.id)
                            )
                        }
                    }),
                    {}
                )
            };
        });
        setData(sortRowsBySelection(next));
    }, [branches, branchLocations, groups, locations, userLocations]);

    /** Selected Groups */
    const selectedGroups = useMemo(
        () =>
            groups.reduce(
                (aggregate, item) => ({
                    ...aggregate,
                    [item.name]: countBy(
                        data,
                        (row) => row[item.name] && row[item.name].selected
                    )
                }),
                {}
            ),
        [groups, data]
    );
    /** Selecting single group */
    const handleGroupSelect = (locationId: number, groupName: string) => () => {
        const next = data.map((row) =>
            row.locationId === locationId
                ? {
                      ...row,
                      [groupName]: {
                          ...row[groupName],
                          selected: !row[groupName].selected
                      }
                  }
                : row
        );
        setData(sortRowsBySelection(next));
    };

    /** Selecting all of the group */
    const handleGroupSelectAll = (groupName: string) => () => {
        const allSelected = selectedGroups[groupName].true === data.length;
        const next = data.reduce((aggregate, item) => {
            const nextGroup = item[groupName];
            nextGroup.selected = !allSelected;
            return [...aggregate, { ...item, [groupName]: nextGroup }];
        }, []);
        setData(sortRowsBySelection(next));
    };

    const columns = [
        {
            accessorKey: "location",
            header: strings("location"),
            size: 350,
            minSize: 150,
            enableResizing: true,
            enableClickToCopy: false,
            muiTableBodyCellProps: ({ cell }) => ({
                title: cell.getValue()
            })
        },
        {
            accessorKey: "branches",
            header: strings("branches"),
            size: 250,
            minSize: 120,
            enableResizing: true,
            enableClickToCopy: false,
            muiTableBodyCellProps: ({ row }) => ({
                title: row.original.branchNames.join(", ")
            }),
            Cell: ({ row }) =>
                row.original.branchNames.map((name) => (
                    <div key={`${row.original.locationId}-branches-${name}`}>
                        <Typography variant="caption">{name}</Typography>
                    </div>
                ))
        },
        ...groups.map((item) => ({
            accessorKey: item.name,
            header: item.name,
            enableSorting: false,
            enableColumnFilter: false,
            enableColumnActions: false,
            enableResizing: false,
            size: 150,
            minSize: 90,
            maxSize: 150,
            accessorFn: (row) => row[item.name]?.selected ?? false,
            muiTableBodyCellProps: { align: "center" },
            muiTableHeadCellProps: { align: "center" },
            Cell: ({ row }) => (
                <Checkbox
                    checked={Boolean(row.original[item.name]?.selected)}
                    onChange={handleGroupSelect(
                        row.original.locationId,
                        item.name
                    )}
                />
            ),
            Header: () => (
                <Button
                    color={
                        selectedGroups[item.name]?.true === data.length
                            ? "primary"
                            : "secondary"
                    }
                    onClick={handleGroupSelectAll(item.name)}
                >
                    {item.name}
                </Button>
            )
        }))
    ];

    return {
        columns,
        data
    };
};
